import { zodResolver } from "@hookform/resolvers/zod";
import { Users } from "lucide-react";
import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useUpdateContactDetailsMutation } from "../../../api/useUpdateContactDetailsMutation";
import ClientConfigCard from "../components/ClientConfigCard";
import { useEditMode } from "../hooks/useEditMode";

import { useClientSchema } from "@/components/forms/schemas/clientSchema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useAuth } from "@/lib/hooks/useAuth";
import useToast from "@/lib/hooks/useToast";
import { ClientType } from "@/types/client/ClientType";

const SECTION_NAME = "contact";

type ContactDetailsCardProps = {
  client?: ClientType;
  clientId: string;
};

const ContactDetailsCard: React.FC<ContactDetailsCardProps> = ({
  client,
  clientId,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const { isEditMode, editMode, viewMode } = useEditMode(SECTION_NAME);
  const [originalValues, setOriginalValues] = useState<{
    firstName?: string;
    lastName?: string;
    infix?: string;
    emailAddress?: string;
    phoneNumber?: string;
    jobTitle?: string;
  }>({});

  const { clientInfo } = useClientSchema();
  const updateMutation = useUpdateContactDetailsMutation(
    clientId,
    tenantId || ""
  );

  // Create schema that only validates contact detail fields (exclude 'name')
  const contactDetailsSchema = clientInfo.omit({ name: true });

  const methods = useForm<z.infer<typeof contactDetailsSchema>>({
    resolver: zodResolver(contactDetailsSchema),
    defaultValues: {
      firstName: client?.firstName || "",
      lastName: client?.lastName || "",
      infix: client?.infix || "",
      emailAddress: client?.emailAddress || "",
      phoneNumber: client?.phoneNumber || "",
      jobTitle: client?.jobTitle || "",
    },
  });

  const { watch, getValues, setValue, setError, handleSubmit } = methods;

  const firstName = watch("firstName") || client?.firstName || "";
  const infix = watch("infix") || client?.infix || "";
  const lastName = watch("lastName") || client?.lastName || "";
  const emailAddress = watch("emailAddress") || client?.emailAddress || "";
  const phoneNumber = watch("phoneNumber") || client?.phoneNumber || "";
  const jobTitle = watch("jobTitle") || client?.jobTitle || "";

  const handleEdit = useCallback(() => {
    const current = getValues();
    const hasChanges =
      isEditMode &&
      (current.firstName !== originalValues.firstName ||
        current.lastName !== originalValues.lastName ||
        current.infix !== originalValues.infix ||
        current.emailAddress !== originalValues.emailAddress ||
        current.phoneNumber !== originalValues.phoneNumber ||
        current.jobTitle !== originalValues.jobTitle);

    const canEdit = editMode(hasChanges, () => {
      setValue(
        "firstName",
        originalValues.firstName || client?.firstName || ""
      );
      setValue("lastName", originalValues.lastName || client?.lastName || "");
      setValue("infix", originalValues.infix || client?.infix || "");
      setValue(
        "emailAddress",
        originalValues.emailAddress || client?.emailAddress || ""
      );
      setValue(
        "phoneNumber",
        originalValues.phoneNumber || client?.phoneNumber || ""
      );
      setValue("jobTitle", originalValues.jobTitle || client?.jobTitle || "");
    });

    if (canEdit) {
      setOriginalValues({
        firstName: current.firstName,
        lastName: current.lastName,
        infix: current.infix,
        emailAddress: current.emailAddress,
        phoneNumber: current.phoneNumber,
        jobTitle: current.jobTitle,
      });
    }
  }, [editMode, isEditMode, getValues, setValue, originalValues, client]);

  const handleCancel = useCallback(() => {
    setValue("firstName", originalValues.firstName || client?.firstName || "");
    setValue("lastName", originalValues.lastName || client?.lastName || "");
    setValue("infix", originalValues.infix || client?.infix || "");
    setValue(
      "emailAddress",
      originalValues.emailAddress || client?.emailAddress || ""
    );
    setValue(
      "phoneNumber",
      originalValues.phoneNumber || client?.phoneNumber || ""
    );
    setValue("jobTitle", originalValues.jobTitle || client?.jobTitle || "");
    viewMode();
  }, [originalValues, client, setValue, viewMode]);

  const handleSave = useCallback(() => {
    const formData = getValues();
    const sectionData = {
      firstName: formData.firstName?.trim() || "",
      lastName: formData.lastName?.trim() || "",
      infix: formData.infix?.trim() || "",
      emailAddress: formData.emailAddress?.trim() || "",
      phoneNumber: formData.phoneNumber?.trim() || "",
      jobTitle: formData.jobTitle?.trim() || "",
      tenantId: tenantId || undefined,
    };

    updateMutation.mutate(sectionData, {
      onSuccess: () => {
        // Update form values to reflect trimmed state
        setValue("firstName", sectionData.firstName);
        setValue("lastName", sectionData.lastName);
        setValue("infix", sectionData.infix);
        setValue("emailAddress", sectionData.emailAddress);
        setValue("phoneNumber", sectionData.phoneNumber);
        setValue("jobTitle", sectionData.jobTitle);
        setOriginalValues({
          firstName: sectionData.firstName,
          lastName: sectionData.lastName,
          infix: sectionData.infix,
          emailAddress: sectionData.emailAddress,
          phoneNumber: sectionData.phoneNumber,
          jobTitle: sectionData.jobTitle,
        });
        viewMode();
        showToast(
          t("admin.client.clientAction.success", { action: "updated" }),
          "success"
        );
      },
      onError: (error) => {
        handleProblemDetailsError({
          error,
          t,
          setError,
          formValues: getValues(),
          showToast,
        });
      },
    });
  }, [
    getValues,
    updateMutation,
    tenantId,
    t,
    showToast,
    setError,
    viewMode,
    setValue,
  ]);

  const onSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(handleSave)(e);
    },
    [handleSubmit, handleSave]
  );

  const editContent = (
    <div className="space-y-4">
      <FormField
        control={methods.control}
        name="firstName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.firstName")}
              <span className="text-destructive ml-1">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder={t("admin.client.addClient.clientInfo.firstName")}
                autoFocus={isEditMode}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={methods.control}
        name="infix"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.infix")}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder={t("admin.client.addClient.clientInfo.infix")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={methods.control}
        name="lastName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.lastName")}
              <span className="text-destructive ml-1">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder={t("admin.client.addClient.clientInfo.lastName")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={methods.control}
        name="emailAddress"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.emailAddress")}
              <span className="text-destructive ml-1">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder={t(
                  "admin.client.addClient.clientInfo.emailAddress"
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={methods.control}
        name="phoneNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.phoneNumber")}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder="088 123 45 67"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={methods.control}
        name="jobTitle"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.jobTitle")}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                placeholder={t("admin.client.addClient.clientInfo.jobTitle")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );

  const viewContent = (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientInfo.firstName")}
        </label>
        <div className="text-base font-normal text-foreground">
          {firstName || "-"}
        </div>
      </div>
      {infix && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("admin.client.addClient.clientInfo.infix")}
          </label>
          <div className="text-base font-normal text-foreground">{infix}</div>
        </div>
      )}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientInfo.lastName")}
        </label>
        <div className="text-base font-normal text-foreground">
          {lastName || "-"}
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientInfo.emailAddress")}
        </label>
        <div className="text-base font-normal text-foreground">
          {emailAddress || "-"}
        </div>
      </div>
      {phoneNumber && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("admin.client.addClient.clientInfo.phoneNumber")}
          </label>
          <div className="text-base font-normal text-foreground">
            {phoneNumber}
          </div>
        </div>
      )}
      {jobTitle && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("admin.client.addClient.clientInfo.jobTitle")}
          </label>
          <div className="text-base font-normal text-foreground">
            {jobTitle}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <FormProvider {...methods}>
      <ClientConfigCard
        title={t("admin.client.editClientTabs.contactDetails")}
        icon={Users}
        isEditMode={isEditMode}
        isPending={updateMutation.isPending}
        onEdit={handleEdit}
        onCancel={handleCancel}
        editContent={editContent}
        onSubmit={onSubmitHandler}
      >
        {viewContent}
      </ClientConfigCard>
    </FormProvider>
  );
};

export default ContactDetailsCard;
